import { wrapText } from '../drawing.utils';
import { getCanvasRef, getContextRef } from '../drawing.refs';
import type { TextBlock } from '../drawing.types';
import type { DrawingStateCreator, DrawingOpsSlice } from './types';

export const createDrawingOpsSlice: DrawingStateCreator<DrawingOpsSlice> = (
  set,
  get
) => ({
  textBlocks: {
    blocks: [],
    activeBlockId: null,
  },

  startDrawing: (point, isPanning = false) =>
    set((state) => {
      if (isPanning || state.modifiers.spacePressed) {
        state.drawing.isPanning = true;
        state.drawing.panStart = point;
      } else {
        state.drawing.isDrawing = true;
        state.drawing.lastPoint = point;
        state.drawing.lastDrawnPoint = point;
        state.drawing.isErasing = state.tool === 'eraser';

        // Don't recalculate offset here - it's set when source is selected
        // This keeps the offset consistent between drawing sessions
      }
    }),

  continueDrawing: (point) => {
    const state = get();
    const { isDrawing, isPanning, lastPoint, panStart, lastDrawnPoint } =
      state.drawing;

    const contextRef = getContextRef();
    if (!contextRef) return;

    if (isPanning && panStart) {
      set((draft) => {
        draft.canvas.transform.pan.x += point.x - panStart.x;
        draft.canvas.transform.pan.y += point.y - panStart.y;
        draft.drawing.panStart = point;
      });
    } else if (isDrawing) {
      if (state.modifiers.shiftPressed && lastDrawnPoint) {
        // For straight line with Shift:
        // Clear the canvas back to last saved state
        const canvasRef = getCanvasRef();
        if (canvasRef && state.history.states.length > 0) {
          // Restore to last state
          const lastState = state.history.states[state.history.currentIndex];
          if (lastState) {
            contextRef.putImageData(lastState.imageData, 0, 0);
          }
        }
        // Draw a straight line from the initial point to current position
        get().drawStraightLine(lastDrawnPoint, point);
        // Update only the current position, not the drawn point
        set((draft) => {
          draft.drawing.lastPoint = point;
        });
      } else if (lastPoint) {
        // Draw normal curved line
        get().drawLine(lastPoint, point);
        set((draft) => {
          draft.drawing.lastPoint = point;
          draft.drawing.lastDrawnPoint = point;
        });
      }
    }
  },

  continuePanning: (screenPoint) => {
    const { isPanning, panStart } = get().drawing;
    if (!isPanning || !panStart) return;

    set((draft) => {
      // Calculate delta in screen coordinates (without transformations)
      const dx = screenPoint.x - panStart.x;
      const dy = screenPoint.y - panStart.y;

      // Apply delta to transform.pan
      draft.canvas.transform.pan.x += dx;
      draft.canvas.transform.pan.y += dy;

      // Update panStart for next iteration
      draft.drawing.panStart = screenPoint;
    });
  },

  endDrawing: () => {
    const state = get();
    const wasDrawing = state.drawing.isDrawing;
    const wasShiftPressed = state.modifiers.shiftPressed;
    const lastPoint = state.drawing.lastPoint;
    const lastDrawnPoint = state.drawing.lastDrawnPoint;

    // If we were drawing a straight line with Shift, finalize it
    if (wasDrawing && wasShiftPressed && lastDrawnPoint && lastPoint) {
      // Draw the final straight line
      get().drawStraightLine(lastDrawnPoint, lastPoint);
    }

    set((draft) => {
      draft.drawing.isDrawing = false;
      draft.drawing.isPanning = false;
      draft.drawing.lastPoint = null;
      draft.drawing.panStart = null;
      draft.drawing.isErasing = false;
    });

    if (wasDrawing) {
      get().saveToHistory('Draw');
      get().checkForChanges();
    }
  },

  drawLine: (from, to) => {
    const { tool, brushSettings, cloneSettings, canvas } = get();

    const contextRef = getContextRef();
    if (!contextRef) return;

    contextRef.save();

    if (tool === 'clone' && cloneSettings.source && cloneSettings.offset) {
      // Clone tool - sample and copy pixels from source to destination
      const brushRadius = brushSettings.size / 2;

      // Interpolate points for smooth line
      const distance = Math.sqrt(
        Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2)
      );
      const steps = Math.max(1, Math.floor(distance / 2));

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const destX = from.x + (to.x - from.x) * t;
        const destY = from.y + (to.y - from.y) * t;

        // Calculate source position based on offset
        const sourceX = destX + cloneSettings.offset.x;
        const sourceY = destY + cloneSettings.offset.y;

        // Ensure source is within canvas bounds
        if (
          sourceX - brushRadius >= 0 &&
          sourceX + brushRadius <= canvas.width &&
          sourceY - brushRadius >= 0 &&
          sourceY + brushRadius <= canvas.height
        ) {
          // Get source area pixels
          const sourceData = contextRef.getImageData(
            Math.max(0, sourceX - brushRadius),
            Math.max(0, sourceY - brushRadius),
            Math.min(brushRadius * 2, canvas.width - (sourceX - brushRadius)),
            Math.min(brushRadius * 2, canvas.height - (sourceY - brushRadius))
          );

          // Create a temporary canvas for circular masking
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = brushRadius * 2;
          tempCanvas.height = brushRadius * 2;
          const tempCtx = tempCanvas.getContext('2d');

          if (tempCtx) {
            // Put source data on temp canvas
            tempCtx.putImageData(sourceData, 0, 0);

            // Apply circular mask
            tempCtx.globalCompositeOperation = 'destination-in';
            tempCtx.beginPath();
            tempCtx.arc(brushRadius, brushRadius, brushRadius, 0, Math.PI * 2);
            tempCtx.fill();

            // Draw masked source to destination with opacity
            contextRef.globalAlpha = brushSettings.opacity / 100;
            contextRef.drawImage(
              tempCanvas,
              destX - brushRadius,
              destY - brushRadius
            );
          }
        }
      }
    } else {
      // Original pencil/eraser logic
      contextRef.globalCompositeOperation =
        tool === 'eraser' ? 'destination-out' : 'source-over';
      contextRef.lineWidth = brushSettings.size;
      contextRef.globalAlpha = brushSettings.opacity / 100;
      contextRef.fillStyle =
        tool === 'pencil' ? brushSettings.color : 'white';

      // Interpolate points for smooth line
      const distance = Math.sqrt(
        Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2)
      );
      const steps = Math.max(1, Math.floor(distance / 2));

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = from.x + (to.x - from.x) * t;
        const y = from.y + (to.y - from.y) * t;

        contextRef.beginPath();
        contextRef.arc(x, y, brushSettings.size / 2, 0, Math.PI * 2);
        contextRef.fill();
      }
    }

    contextRef.restore();
  },

  drawStraightLine: (from, to) => {
    const { tool, brushSettings } = get();

    const contextRef = getContextRef();
    if (!contextRef) return;

    contextRef.save();
    contextRef.globalCompositeOperation =
      tool === 'eraser' ? 'destination-out' : 'source-over';
    contextRef.lineWidth = brushSettings.size;
    contextRef.lineCap = 'round';
    contextRef.strokeStyle =
      tool === 'pencil' ? brushSettings.color : 'white';
    contextRef.globalAlpha = brushSettings.opacity / 100;

    contextRef.beginPath();
    contextRef.moveTo(from.x, from.y);
    contextRef.lineTo(to.x, to.y);
    contextRef.stroke();

    contextRef.restore();
  },

  createTextBlock: (point) =>
    set((state) => {
      const id = `text-${Date.now()}-${Math.random()}`;
      const width = 80; // Initial width for empty text (easier to interact with)
      const height = 30; // Minimum height
      const newBlock: TextBlock = {
        id,
        // Tap point is the center of the created block
        x: point.x - width / 2,
        y: point.y - height / 2,
        width,
        height,
        text: '',
        isManualWidth: false, // Start in auto-expand mode
      };
      state.textBlocks.blocks.push(newBlock);
      state.textBlocks.activeBlockId = id;
      state.canvas.hasChanges = true;
    }),

  updateTextBlock: (id, updates) =>
    set((state) => {
      const block = state.textBlocks.blocks.find((b) => b.id === id);
      if (block) {
        Object.assign(block, updates);
        state.canvas.hasChanges = true;
      }
    }),

  deleteTextBlock: (id) =>
    set((state) => {
      state.textBlocks.blocks = state.textBlocks.blocks.filter(
        (b) => b.id !== id
      );
      if (state.textBlocks.activeBlockId === id) {
        state.textBlocks.activeBlockId = null;
      }
      state.canvas.hasChanges = true;
    }),

  setActiveTextBlock: (id) =>
    set((state) => {
      const oldId = state.textBlocks.activeBlockId;

      // If switching away from a block (either to null or to another block),
      // check if old block is empty and delete it
      if (oldId && oldId !== id) {
        const oldBlock = state.textBlocks.blocks.find((b) => b.id === oldId);
        if (oldBlock && !oldBlock.text.trim()) {
          state.textBlocks.blocks = state.textBlocks.blocks.filter(
            (b) => b.id !== oldId
          );
          state.canvas.hasChanges = true;
        }
      }

      state.textBlocks.activeBlockId = id;
    }),

  renderTextBlocksToCanvas: () => {
    const { textBlocks } = get();
    const contextRef = getContextRef();
    if (!contextRef) return;

    // Don't render if no text blocks (avoid re-rendering already saved text)
    if (textBlocks.blocks.length === 0) return;

    // Render each text block to canvas
    textBlocks.blocks.forEach((block) => {
      // Trim whitespace and newlines from the entire text
      const trimmedText = block.text.trim();
      if (!trimmedText) return; // Skip empty blocks

      contextRef.save();

      // Ensure proper compositing - replace existing pixels
      contextRef.globalCompositeOperation = 'source-over';
      contextRef.globalAlpha = 1.0;

      // Match CSS exactly: 16px bold sans-serif (reduced from 18px)
      contextRef.font = 'bold 16px sans-serif';
      contextRef.textBaseline = 'top';

      // Render each line
      const lineHeight = 22; // Match CSS lineHeight: 22px (reduced from 24px)
      // Text position must match CSS exactly
      // In CSS overlay, block.x/y is the top-left corner of the TEXT CONTENT
      // (after compensation for parent padding in positioning)
      // So we just need to add the contenteditable padding: 4px top, 6px left
      const paddingLeft = 6;
      const paddingTop = 4;

      // Available width for text wrapping — use measured block.width from DOM
      const availableWidth = block.width - 12; // block.width - contenteditable padding (6px * 2)

      // Split text by explicit line breaks, preserving blank lines
      const explicitLines = trimmedText.split('\n');

      // Collect all lines with their positions for background rendering
      const allLines: Array<{ text: string; y: number }> = [];
      let currentY = 0;

      // Process each explicit line - wrap non-empty lines, preserve blank lines
      explicitLines.forEach((line) => {
        if (line.trim().length === 0) {
          currentY += lineHeight;
        } else {
          const wrappedLines = wrapText(contextRef, line, availableWidth);
          wrappedLines.forEach((wrappedLine) => {
            allLines.push({ text: wrappedLine, y: currentY });
            currentY += lineHeight;
          });
        }
      });

      // Draw background (semi-transparent white)
      // Match CSS: contenteditable has padding '4px 6px' and background
      contextRef.fillStyle = 'rgba(255, 255, 255, 0.75)';

      // Calculate max text width for background
      let maxTextWidth = 0;
      allLines.forEach((line) => {
        const textWidth = contextRef.measureText(line.text).width;
        maxTextWidth = Math.max(maxTextWidth, textWidth);
      });

      // Calculate total height (all lines including blank + top/bottom padding)
      const totalTextHeight = currentY;

      // Draw single background rectangle covering all text
      // Background starts at block.x (text is at block.x + paddingLeft)
      // Background starts at block.y (text is at block.y + paddingTop)
      contextRef.fillRect(
        block.x, // Left edge
        block.y, // Top edge
        maxTextWidth + 12, // Width = max text width + 6px left + 6px right padding
        totalTextHeight + 8 // Height = total text height + 4px top + 4px bottom padding
      );

      // Draw text on top of background
      contextRef.fillStyle = '#000000';
      allLines.forEach((line) => {
        contextRef.fillText(
          line.text,
          block.x + paddingLeft,
          block.y + paddingTop + line.y
        );
      });

      contextRef.restore();
    });
  },
});
